/**
 * PeripheralForm  Formulario full-screen para periféricos de red misceláneos.
 *
 * Diseño coherente con PrinterForm / WifiAccessPointForm:
 *   TopBar  SmartBar  Body (title + dv-tabs-nav + tab panes)
 *
 * Tabs:
 *   1. Identificación  código, fabricante (libre), modelo (libre), tipo, estado, serie, fecha compra
 *   2. Conectividad    MAC, IP, URL gestión, WiFi (SSID), notas
 *   3. Ubicación       sede, sala, planta
 *   4. IPAM            ElementIpPanel (sólo al editar)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  WifiIcon,
  MapPinIcon,
  PencilSquareIcon,
  PuzzlePieceIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import Dialog from './common/Dialog';
import LocationTreeSelect from './common/LocationTreeSelect';
import ElementIpPanel from './ElementIpPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import DocSmartButton from './DocSmartButton';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import { buildLocationMap, locationPath } from '../utils/locationTree';
import CiActivityPanel from './common/CiActivityPanel';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const STATUS_COLORS = {
  active:         { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
  inactive:       { bg: '#f1f5f9', color: '#64748b', label: 'Inactivo' },
  maintenance:    { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
  decommissioned: { bg: '#fee2e2', color: '#991b1b', label: 'Dado de baja' },
};

const STATUS_CYCLE = ['active', 'maintenance', 'inactive', 'decommissioned'];

const EMPTY_FORM = {
  code: '', name: '', manufacturer: '', model: '',
  peripheral_type_id: '', status: 'active',
  serial_number: '', purchase_date: '',
  ip_address: '', mac_address: '', management_url: '',
  has_wifi: false, wifi_ssid: '',
  room: '', floor: '', location_id: '', notes: '',
};

export default function PeripheralForm({ peripheral, companyId, initialMode = 'create', onClose, onSave, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = !!peripheral?.id;

  // Modo lectura por defecto en las fichas existentes (estándar del CMDB).
  const [editing, setEditing] = useState(!isEditing || initialMode === 'edit');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [form,    setForm]    = useState(EMPTY_FORM);
  const [isDirty, setIsDirty] = useState(false);

  const [tab,     setTab]     = useState(isCards ? 'resumen' : 'identificacion');
  const [accessCount, setAccessCount] = useState(0);
  const [saving,  setSaving]  = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar (después de `saving`: un
  // objeto que lo lea antes de su `const` revienta con ReferenceError).
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este periférico',
    actionLabel: 'pasas a otro periférico o cierras la ficha',
    save: () => handleSave(),
  });
  const [suggesting, setSuggesting] = useState(false);
  const [apiError,setApiError]= useState(null);

  const [locations,       setLocations]       = useState([]);
  const [peripheralTypes, setPeripheralTypes] = useState([]);
  const [availableSsids,  setAvailableSsids]  = useState([]);

  const [addingType,  setAddingType]  = useState(false);
  const [newTypeName, setNewTypeName] = useState('');

  //  Load catalogs 
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
    api.getPeripheralTypes(companyId).then(r => setPeripheralTypes(r.data || [])).catch(() => {});
    api.getWifiSSIDs(companyId).then(r => setAvailableSsids(r.data || [])).catch(() => {});
  }, [companyId]);

  useEffect(() => {
    if (peripheral) {
      setForm({
        code:               peripheral.code               || '',
        name:               peripheral.name               || '',
        manufacturer:       peripheral.manufacturer       || '',
        model:              peripheral.model              || '',
        peripheral_type_id: peripheral.peripheral_type_id ? String(peripheral.peripheral_type_id) : '',
        status:             peripheral.status             || 'active',
        serial_number:      peripheral.serial_number      || '',
        purchase_date:      peripheral.purchase_date      || '',
        ip_address:         peripheral.ip_address         || '',
        mac_address:        peripheral.mac_address        || '',
        management_url:     peripheral.management_url     || '',
        has_wifi:           peripheral.has_wifi           ?? false,
        wifi_ssid:          peripheral.wifi_ssid          || '',
        room:               peripheral.room               || '',
        floor:              peripheral.floor              || '',
        location_id:        peripheral.location_id        ? String(peripheral.location_id) : '',
        notes:              peripheral.notes              || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setIsDirty(false);
  }, [peripheral]);

  //  Handlers 
  const sf = useCallback((k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setIsDirty(true);
  }, []);

  /* Generador de código con la nomenclatura de la empresa (§12.C8). */
  const handleSuggestCode = async () => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const resp = await api.suggestName({
        type: 'peripheral',
        companyId,
        locationId: form.location_id || undefined,
        rackId: form.rack_id || undefined,
        consume: true,
      });
      const s = resp?.data?.suggested_name;
      if (s) {
        setForm(prev => ({ ...prev, code: s, name: prev.name || s }));
        setIsDirty(true);
      }
    } catch (err) {
      setApiError(err.message || 'No se pudo generar el código');
    } finally {
      setSuggesting(false);
    }
  };
  const handleChange = useCallback(e => {
    const { name, value, type, checked } = e.target;
    sf(name, type === 'checkbox' ? checked : value);
  }, [sf]);

  const handleDiscard = () => {
    if (peripheral) {
      setForm({
        code:               peripheral.code               || '',
        name:               peripheral.name               || '',
        manufacturer:       peripheral.manufacturer       || '',
        model:              peripheral.model              || '',
        peripheral_type_id: peripheral.peripheral_type_id ? String(peripheral.peripheral_type_id) : '',
        status:             peripheral.status             || 'active',
        serial_number:      peripheral.serial_number      || '',
        purchase_date:      peripheral.purchase_date      || '',
        ip_address:         peripheral.ip_address         || '',
        mac_address:        peripheral.mac_address        || '',
        management_url:     peripheral.management_url     || '',
        has_wifi:           peripheral.has_wifi           ?? false,
        wifi_ssid:          peripheral.wifi_ssid          || '',
        room:               peripheral.room               || '',
        floor:              peripheral.floor              || '',
        location_id:        peripheral.location_id        ? String(peripheral.location_id) : '',
        notes:              peripheral.notes              || '',
      });
    } else {
      onClose();
    }
    setIsDirty(false);
    setApiError(null);
    // Descartar en una ficha ya guardada vuelve a LECTURA (no la cierra).
    if (isEditing) setEditing(false);
  };

  const handleSave = async () => {
    if (!editing) return false;
    if (!form.name.trim()) { setApiError('El nombre es obligatorio'); return false; }
    setSaving(true); setApiError(null);
    try {
      const payload = {
        ...form,
        location_id:        form.location_id        ? parseInt(form.location_id, 10)        : null,
        peripheral_type_id: form.peripheral_type_id ? parseInt(form.peripheral_type_id, 10) : null,
        purchase_date:      form.purchase_date || null,
        has_wifi:           form.has_wifi,
        wifi_ssid:          form.has_wifi ? (form.wifi_ssid || null) : null,
      };
      let creado = null;
      if (isEditing) {
        await api.updatePeripheral(peripheral.id, payload, companyId);
      } else {
        creado = registroGuardado(await api.createPeripheral(payload, companyId));
      }
      setIsDirty(false);
      // Alta: el listado adopta el registro creado, así la ficha sigue abierta
      // ya con id (y se encienden las zonas que necesitan estar guardado).
      onSave?.(creado);
      return true;
    } catch (err) {
      setApiError(err.message || 'Error al guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await api.deletePeripheral(peripheral.id, companyId);
      onSave?.();
      onClose();
    } catch (err) {
      setApiError(err.message || 'Error al eliminar');
    }
  };

  const saveType = async () => {
    const name = newTypeName.trim();
    if (!name) return;
    try {
      const res = await api.createPeripheralType({ name }, companyId);
      const created = res.data;
      setPeripheralTypes(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      sf('peripheral_type_id', String(created.id));
      setNewTypeName('');
      setAddingType(false);
    } catch (e) { setApiError(e.message); }
  };

  //  Computed 
  const statusInfo   = STATUS_COLORS[form.status] || STATUS_COLORS.active;
  const currentType  = peripheralTypes.find(t => String(t.id) === form.peripheral_type_id);
  const locationName = locations.find(l => String(l.id) === form.location_id)?.name || '';
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacion = form.location_id ? locationPath(mapaUbicaciones, form.location_id) : '';

  const tabs = [
    { key: 'identificacion', label: 'Identificación' },
    { key: 'ubicacion',      label: 'Ubicación' },
    { key: 'access',         label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
    { key: 'conectividad',   label: 'Red / IP' },
  ];

  // En tarjetas no hay pestañas «Identificación» ni «Ubicación»: van al Resumen.
  const tabsDef = isCards
    ? [
        { key: 'resumen',      label: 'General' },
        { key: 'conectividad', label: 'Red / IP' },
        ...(isEditing ? [{ key: 'access', label: tabs[2].label }] : []),
      ]
    : tabs;

  return (
    <div className="dv-overlay">

      {/*  TopBar  */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon style={{ width: 15, height: 15 }} />
          Otros Periféricos
        </button>
        <DetailCrumb label={form.name} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Periférico"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {apiError && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
             {apiError}
          </span>
        )}
        <div className="dv-topbar-actions">
          {isCards ? (
            <>
              {!editing && isEditing && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving} title="Editar la ficha">
                  <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
              {isEditing && (
                <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
                  <TrashIcon style={{ width: 14, height: 14 }} />
                </button>
              )}
            </>
          ) : (
          <>
          <span
            className="dv-status-badge"
            style={{ background: statusInfo.bg, color: statusInfo.color, cursor: editing ? 'pointer' : 'default' }}
            title={editing ? 'Clic para cambiar estado' : undefined}
            onClick={() => {
              if (!editing) return;
              const idx = STATUS_CYCLE.indexOf(form.status);
              sf('status', STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]);
            }}
          >
             {statusInfo.label}
          </span>
          {!editing && isEditing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving}>
              <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
            </button>
          )}
          {editing && isEditing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon style={{ width: 14, height: 14 }} /> Descartar
            </button>
          )}
          <button
            className="dv-btn dv-btn-primary"
            type="button"
            onClick={handleSave}
            disabled={!editing || saving || (isEditing && !isDirty)}
          >
            {saving
              ? <span className="spinner-border spinner-border-sm" />
              : <CheckIcon style={{ width: 14, height: 14 }} />}
            {saving ? 'Guardando' : 'Guardar'}
          </button>
          {isEditing && (
            <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
              <TrashIcon style={{ width: 14, height: 14 }} />
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {/*  SmartBar (solo en la ficha clásica)  */}
      {!isCards && (
      <div className="dv-smartbar">

        <div className="dv-smart-btn" title={currentType?.name || 'Sin tipo'}>
          <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}></span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentType?.name || ''}
          </span>
          <span className="dv-smart-btn-label">Tipo</span>
        </div>

        <div className="dv-smart-sep" />

        <button className="dv-smart-btn" type="button" onClick={() => setTab('identificacion')}>
          <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}></span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.manufacturer || ''}
          </span>
          <span className="dv-smart-btn-label">Fabricante</span>
        </button>

        <div className="dv-smart-sep" />

        <button className="dv-smart-btn" type="button" onClick={() => setTab('identificacion')}>
          <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}></span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.model || ''}
          </span>
          <span className="dv-smart-btn-label">Modelo</span>
        </button>

        {form.ip_address && (
          <>
            <div className="dv-smart-sep" />
            <button className="dv-smart-btn" type="button" onClick={() => setTab('conectividad')}>
              <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}></span>
              <span className="dv-smart-btn-value" style={{ color: '#d97706', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                {form.ip_address}
              </span>
              <span className="dv-smart-btn-label">IP</span>
            </button>
          </>
        )}

        {form.has_wifi && (
          <>
            <div className="dv-smart-sep" />
            <button className="dv-smart-btn" type="button" onClick={() => setTab('conectividad')}>
              <span className="dv-smart-btn-icon">
                <WifiIcon style={{ width: 18, height: 18, color: '#059669' }} />
              </span>
              <span className="dv-smart-btn-value" style={{ color: '#059669', fontSize: '0.8rem', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {form.wifi_ssid || 'WiFi'}
              </span>
              <span className="dv-smart-btn-label">WiFi</span>
            </button>
          </>
        )}

        {locationName && (
          <>
            <div className="dv-smart-sep" />
            <button className="dv-smart-btn" type="button" onClick={() => setTab('ubicacion')}>
              <span className="dv-smart-btn-icon">
                <MapPinIcon style={{ width: 18, height: 18, color: '#dc2626' }} />
              </span>
              <span className="dv-smart-btn-value" style={{ color: '#dc2626', fontSize: '0.82rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {locationName}
              </span>
              <span className="dv-smart-btn-label">Sede</span>
            </button>
          </>
        )}

        {isEditing && (
          <>
            <div className="dv-smart-sep" />
            <DocSmartButton
              elementType="peripheral"
              elementId={peripheral.id}
              elementName={form.name}
              companyId={companyId}
            />
          </>
        )}

      </div>
      )}

      {/*  Body  */}
      <div className="dv-body">
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={e => e.preventDefault()}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<PuzzlePieceIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => sf('name', v)}
                placeholder="Nombre del periférico"
                editing={editing}
                saving={saving}
                onSave={handleSave}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                crumb={[currentType?.name, form.manufacturer, form.model, rutaUbicacion || locationName].filter(Boolean).join(' › ')}
                badges={[
                  { text: statusInfo.label, color: statusInfo.color },
                  ...(form.has_wifi ? [{ text: form.wifi_ssid ? `WiFi: ${form.wifi_ssid}` : 'WiFi' }] : []),
                ]}
                chips={[
                  ...(form.ip_address ? [{ value: form.ip_address, label: 'IP' }] : []),
                  ...(form.mac_address ? [{ value: form.mac_address, label: 'MAC' }] : []),
                  ...(form.management_url ? [{ value: 'Web', label: 'gestión', onClick: () => window.open(form.management_url, '_blank', 'noopener') }] : []),
                  ...(accessCount > 0 ? [{ value: accessCount, label: 'accesos', onClick: () => setTab('access') }] : []),
                ]}
                extra={peripheral?.id ? (
                  <DocSmartButton elementType="peripheral" elementId={peripheral.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <input
                className="dv-record-title"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Nombre del periférico"
              />
            )}
            tabs={(
              <div className="dv-tabs-nav">
            {tabsDef.map(t => (
              <TabButton
                key={t.key}
                type="button"
                className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </TabButton>
            ))}
              </div>
            )}
          >
          <fieldset disabled={!editing} className="dv-fieldset">

          {/*  Resumen (solo tarjetas)  */}
          {isCards && tab === 'resumen' && (
            <div className="dv-tab-pane">
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>Tipo</span><b style={{ fontSize: '0.95rem' }}>{currentType?.name || '—'}</b></div>
                <div className="sf2-kpi"><span>IP</span><b style={{ fontSize: '0.95rem' }}>{form.ip_address || '—'}</b></div>
                <div className="sf2-kpi"><span>WiFi</span><b style={{ fontSize: '0.95rem' }}>{form.has_wifi ? (form.wifi_ssid || 'Sí') : 'No'}</b></div>
                <div className="sf2-kpi"><span>Accesos</span><b>{accessCount}</b></div>
                <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{statusInfo.label}</b></div>
              </div>

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Identificación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Código</label>
                      {editing
                        ? (
                          <div className="sf2-input-row">
                            <input value={form.code} onChange={e => sf('code', e.target.value)} placeholder="PERIPH-001" />
                            <button
                              type="button"
                              className="sf2-icon-btn"
                              onClick={handleSuggestCode}
                              disabled={suggesting || !companyId || saving}
                              title="Generar el código con la nomenclatura"
                              aria-label="Generar código"
                            >
                              {suggesting ? <span className="dv-spinner" /> : <SparklesIcon style={{ width: 15, height: 15 }} />}
                            </button>
                          </div>
                        )
                        : <div className="v">{form.code || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Estado</label>
                      {editing ? (
                        <select value={form.status} onChange={e => sf('status', e.target.value)}>
                          {STATUS_CYCLE.map(s => <option key={s} value={s}>{STATUS_COLORS[s]?.label || s}</option>)}
                        </select>
                      ) : <div className="v">{statusInfo.label}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Tipo de periférico</label>
                      {editing ? (
                        <select value={form.peripheral_type_id} onChange={e => sf('peripheral_type_id', e.target.value)}>
                          <option value="">— Sin tipo —</option>
                          {peripheralTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      ) : <div className="v">{currentType?.name || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Número de serie</label>
                      {editing
                        ? <input value={form.serial_number} onChange={e => sf('serial_number', e.target.value)} />
                        : <div className="v">{form.serial_number || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Fabricante</label>
                      {editing
                        ? <input value={form.manufacturer} onChange={e => sf('manufacturer', e.target.value)} placeholder="Samsung, LG…" />
                        : <div className="v">{form.manufacturer || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Modelo</label>
                      {editing
                        ? <input value={form.model} onChange={e => sf('model', e.target.value)} />
                        : <div className="v">{form.model || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Fecha de compra</label>
                      {editing
                        ? <input type="date" value={form.purchase_date} onChange={e => sf('purchase_date', e.target.value)} />
                        : <div className="v">{form.purchase_date || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Ubicación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Ubicación</label>
                      {editing ? (
                        <LocationTreeSelect
                          locations={locations}
                          value={form.location_id}
                          onChange={(id) => sf('location_id', id)}
                          placeholder="— Sin ubicación —"
                        />
                      ) : <div className="v">{rutaUbicacion || locationName || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Planta</label>
                      {editing
                        ? <input value={form.floor} onChange={e => sf('floor', e.target.value)} placeholder="1ª, Baja…" />
                        : <div className="v">{form.floor || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Sala</label>
                      {editing
                        ? <input value={form.room} onChange={e => sf('room', e.target.value)} placeholder="Sala principal" />
                        : <div className="v">{form.room || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Red{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Dirección IP</label>
                      {editing
                        ? <input value={form.ip_address} onChange={e => sf('ip_address', e.target.value)} placeholder="192.168.1.100" />
                        : <div className="v">{form.ip_address || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>MAC</label>
                      {editing
                        ? <input value={form.mac_address} onChange={e => sf('mac_address', e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" />
                        : <div className="v">{form.mac_address || '—'}</div>}
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>URL de gestión</label>
                      {editing
                        ? <input value={form.management_url} onChange={e => sf('management_url', e.target.value)} placeholder="http://192.168.1.100" />
                        : (form.management_url
                          ? <div className="v"><a href={form.management_url} target="_blank" rel="noopener noreferrer">{form.management_url}</a></div>
                          : <div className="v">—</div>)}
                    </div>
                    <div className="sf2-field">
                      <label>WiFi</label>
                      <label className="sf2-switch sf2-switch--sm">
                        <input type="checkbox" checked={!!form.has_wifi} onChange={e => sf('has_wifi', e.target.checked)} disabled={!editing} />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{form.has_wifi ? 'Sí' : 'No'}</span>
                      </label>
                    </div>
                    {form.has_wifi && (
                      <div className="sf2-field">
                        <label>SSID</label>
                        {editing ? (
                          <select value={form.wifi_ssid} onChange={e => sf('wifi_ssid', e.target.value)}>
                            <option value="">— Sin SSID —</option>
                            {availableSsids.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                          </select>
                        ) : <div className="v">{form.wifi_ssid || '—'}</div>}
                      </div>
                    )}
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Notas</label>
                      {editing ? (
                        <textarea className="dv-input" rows={3} value={form.notes} onChange={e => sf('notes', e.target.value)} placeholder="Observaciones…" />
                      ) : <div className="v">{form.notes || '—'}</div>}
                    </div>
                  </div>
                  <span className="sf2-link" role="button" tabIndex={0}
                    onClick={() => setTab('conectividad')}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setTab('conectividad'); } }}
                  >Ver conectividad e IPAM →</span>
                </div>
              </div>
            </div>
          )}

          {/*  Identificación  */}
          {tab === 'identificacion' && (
            <div className="dv-tab-pane">

              <div className="dv-section">
                <div className="dv-section-title">Datos generales</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Código</label>
                    <input className="dv-input" name="code" value={form.code} onChange={handleChange} placeholder="PERIPH-001" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Número de serie</label>
                    <input className="dv-input" name="serial_number" value={form.serial_number} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Fecha de compra</label>
                    <input className="dv-input" type="date" name="purchase_date" value={form.purchase_date} onChange={handleChange} />
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Tipo de dispositivo</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Tipo de periférico</label>
                    {addingType ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          className="dv-input"
                          value={newTypeName}
                          onChange={e => setNewTypeName(e.target.value)}
                          placeholder="Ej: Televisión, Pantalla, Microondas"
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveType();
                            if (e.key === 'Escape') { setAddingType(false); setNewTypeName(''); }
                          }}
                          autoFocus
                          style={{ flex: 1 }}
                        />
                        <button className="dv-btn dv-btn-primary" type="button" onClick={saveType}>
                          <CheckIcon style={{ width: 14, height: 14 }} /> Añadir
                        </button>
                        <button className="dv-btn dv-btn-secondary" type="button" onClick={() => { setAddingType(false); setNewTypeName(''); }}>
                          <XMarkIcon style={{ width: 14, height: 14 }} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select className="dv-select" style={{ flex: 1 }} name="peripheral_type_id" value={form.peripheral_type_id} onChange={handleChange}>
                          <option value=""> Sin tipo </option>
                          {peripheralTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        <button className="dv-btn dv-btn-secondary" type="button" title="Nuevo tipo" onClick={() => setAddingType(true)}>
                          + Nuevo
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Fabricante y Modelo</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Fabricante</label>
                    <input
                      className="dv-input"
                      name="manufacturer"
                      value={form.manufacturer}
                      onChange={handleChange}
                      placeholder="Samsung, LG, Philips"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Modelo</label>
                    <input
                      className="dv-input"
                      name="model"
                      value={form.model}
                      onChange={handleChange}
                      placeholder="UN55TU7000, 65UP7560"
                    />
                  </div>
                </div>
              </div>

            </div>
          )}

          {/*  Conectividad  */}
          {tab === 'conectividad' && (
            <div className="dv-tab-pane">

              <div className="dv-section">
                <div className="dv-section-title">Dirección de red</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Dirección IP <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>(se sincroniza con IPAM)</span>
                    </label>
                    <input className="dv-input" name="ip_address" value={form.ip_address} onChange={handleChange} placeholder="192.168.1.100" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">MAC Address</label>
                    <input className="dv-input" name="mac_address" value={form.mac_address} onChange={handleChange} placeholder="AA:BB:CC:DD:EE:FF" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      URL de gestión <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>(interfaz web)</span>
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        className="dv-input"
                        name="management_url"
                        value={form.management_url}
                        onChange={handleChange}
                        placeholder="http://192.168.1.100 o https://device.local"
                        style={{ flex: 1 }}
                      />
                      {form.management_url && (
                        <a
                          href={form.management_url}
                          target="_blank"
                          rel="noreferrer"
                          className="dv-btn dv-btn-secondary"
                          style={{ whiteSpace: 'nowrap', textDecoration: 'none' }}
                        >
                          Abrir 
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Conexión WiFi</div>
                <div className="row g-3">
                  <div className="col-12">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                      <input
                        type="checkbox"
                        name="has_wifi"
                        checked={form.has_wifi}
                        onChange={handleChange}
                        style={{ width: 16, height: 16 }}
                      />
                      <WifiIcon style={{ width: 16, height: 16, color: form.has_wifi ? '#059669' : '#94a3b8' }} />
                      Conectado por WiFi
                    </label>
                  </div>
                  {form.has_wifi && (
                    <div className="col-md-6">
                      <label className="form-label">SSID (red WiFi)</label>
                      {availableSsids.length > 0 ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <select
                            className="dv-select"
                            style={{ flex: 1 }}
                            value={form.wifi_ssid}
                            onChange={e => sf('wifi_ssid', e.target.value)}
                          >
                            <option value=""> Seleccionar SSID </option>
                            {[...new Map(availableSsids.map(s => [s.name, s])).values()].map(s => (
                              <option key={s.id} value={s.name}>{s.name}</option>
                            ))}
                          </select>
                          <input
                            className="dv-input"
                            value={form.wifi_ssid}
                            onChange={e => sf('wifi_ssid', e.target.value)}
                            placeholder="o escribe el SSID"
                            style={{ flex: 1 }}
                          />
                        </div>
                      ) : (
                        <input
                          className="dv-input"
                          value={form.wifi_ssid}
                          onChange={e => sf('wifi_ssid', e.target.value)}
                          placeholder="Nombre de la red WiFi"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* IPAM — Asignaciones de IP */}
              <div className="dv-section">
                <div className="dv-section-title">IPAM — Asignaciones de IP</div>
                <ElementIpPanel
                  objectType="peripheral"
                  objectId={peripheral?.id}
                  companyId={companyId}
                  elementName={peripheral?.name || form.name}
                />
              </div>
            </div>
          )}
          {/*  Accesos  */}
          {tab === 'access' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <DeviceAccessPanel
                  objectType="peripheral"
                  objectId={peripheral?.id}
                  companyId={companyId}
                  elementName={peripheral?.name || form.name}
                  defaultHost={form.ip_address}
                  onCountChange={setAccessCount}
                  canEdit={editing}
                />
              </div>
            </div>
          )}

          {/*  Ubicación  */}
          {tab === 'ubicacion' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Ubicación física</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Sede / Ubicación</label>
                  <LocationTreeSelect
                    locations={locations}
                    value={form.location_id}
                    onChange={(id) => handleChange({ target: { name: 'location_id', value: id } })}
                    placeholder=" Sin sede "
                  />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Sala / Zona</label>
                    <input className="dv-input" name="room" value={form.room} onChange={handleChange} placeholder="Sala de juntas, Recepción" />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Planta</label>
                    <input className="dv-input" name="floor" value={form.floor} onChange={handleChange} placeholder="2" />
                  </div>
                </div>
              </div>
            </div>
          )}

          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={handleSave}
          />
          </FormCardsShell>
        </form>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="peripheral"
          ciId={peripheral?.id}
          tipoLegible="el periférico"
        />
      </div>

      {/* Diálogo: eliminar periférico */}
      {showDeleteConfirm && (
        <Dialog
          title="Eliminar periférico"
          onClose={() => setShowDeleteConfirm(false)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="list-btn-danger" onClick={handleDelete} disabled={saving}>
                Eliminar
              </button>
            </>
          )}
        >
          <p>
            ¿Seguro que quieres eliminar <strong>{form.name || form.code}</strong>?{' '}
            Se eliminarán también sus conexiones. No se puede deshacer.
          </p>
        </Dialog>
      )}

    </div>
  );
}
